import React, { useMemo, useState } from "react";

import { Box } from "@chakra-ui/react";
import {
  IBusinessDetailDocument,
  IBusinessDetails,
} from "api/business-user-management/types";
import { BusinessContentItem } from "features/business-compliance/selected-business/BusinessContentItem";
import { BusinessDocumentView } from "features/business-compliance/selected-business/BusinessDocumentView";
import {
  IBusinessContentItem,
  IBusinessDocumentView,
} from "features/business-compliance/selected-business/types";
import { getBusinessDocumentTitle } from "utils";
import { REGISTERED_BUSINESS_COMPANY_TYPE, START_INDEX } from "utils/constants";

import { ImageViewer } from "ui/image-viewer";

import { IBusinessDetailsDocumentsProps } from "./types";

export const addressDocumentLabel: Record<string, string> = {
  "utility-bill": "Utility Bill (Electricity or Waste Bill)",
  "bank-statement": "Bank Statement from a Commercial Bank",
};

export const unRegisteredBusinessDirectorDocsLabel: Record<string, string> = {
  "national-id": "National ID Card or NIN Slip",
  "international-passport": "International Passport",
  "driver's-license": "Driver's License",
};

const proofOfAddressDocNames = ["utility-bill", "bank-statement"];
const validDocNamesUnregisteredBusiness = [...proofOfAddressDocNames];
const validDocNamesRegisteredBusiness = [
  ...proofOfAddressDocNames,
  "cac",
  "particulars-of-directors",
];

const findAddressDocsLabel = (documents: IBusinessDetailDocument[]) => {
  for (const doc of documents) {
    if (addressDocumentLabel[doc.name]) {
      return addressDocumentLabel[doc.name];
    }
  }
  return "-";
};

const filterMostRecentValidBusinessDocument = (
  businessDocs: IBusinessDetailDocument[],
  business: IBusinessDetails
): IBusinessDetailDocument[] => {
  let latestBankStatement = null;
  let latestUtilityBill = null;
  const validBusinessDocumentNames =
    business?.business?.companyData?.companyType ===
    REGISTERED_BUSINESS_COMPANY_TYPE
      ? validDocNamesRegisteredBusiness
      : validDocNamesUnregisteredBusiness;

  businessDocs.forEach((obj) => {
    if (obj.name === "bank-statement") {
      latestBankStatement = obj;
    } else if (obj.name === "utility-bill") {
      latestUtilityBill = obj;
    }
  });

  const result = businessDocs.filter((obj) => {
    if (obj.name === "bank-statement" || obj.name === "utility-bill") {
      return false; // Remove these temporarily
    }
    return true; // Keep everything else
  });

  if (
    latestUtilityBill &&
    (!latestBankStatement ||
      businessDocs.indexOf(latestUtilityBill) >
        businessDocs.indexOf(latestBankStatement))
  ) {
    result.push(latestUtilityBill); // Add utility bill if it's the most recent
  } else if (latestBankStatement) {
    result.push(latestBankStatement); // Add bank statement if it's the most recent
  }
  return result?.filter(({ name }) =>
    validBusinessDocumentNames.includes(name)
  );
};

export const BusinessDetailsDocuments = (
  props: IBusinessDetailsDocumentsProps
) => {
  const { business, isContentWrapperBelow745, isContentWrapperBelow600 } =
    props;
  const [showBusinessDocumentImages, setShowBusinessDocumentImages] =
    useState(false);
  const [businessDocumentIndex, setBusinessDocumentIndex] =
    useState(START_INDEX);

  const handleBusinessDocumentImageIndexChange = (imageIndex: number) => {
    setBusinessDocumentIndex(imageIndex);
  };

  const handleOpenBusinessDocumentImages = (
    currentBusinessDocIndex: number
  ) => {
    handleBusinessDocumentImageIndexChange(currentBusinessDocIndex);
    setShowBusinessDocumentImages(!showBusinessDocumentImages);
  };

  const handleCloseBusinessDocumentImages = () => {
    setShowBusinessDocumentImages(false);
  };

  const businessDocumentsList: IBusinessContentItem[] = useMemo(
    () => [
      ...(business?.business?.companyData?.companyType ===
      REGISTERED_BUSINESS_COMPANY_TYPE
        ? [
            {
              title: "Country",
              content: business?.business?.companyData?.country || "-",
            },
          ]
        : []),
      {
        title: "Address",
        content: business?.business?.companyData?.address || "-",
      },
      {
        title:
          business?.business?.companyData?.companyType ===
          REGISTERED_BUSINESS_COMPANY_TYPE
            ? "Proof of Address Type"
            : "Document Type",
        content: findAddressDocsLabel(
          business?.business?.companyData?.businessDocuments || []
        ),
      },
      {
        title: "State",
        content: business?.business?.companyData?.state || "-",
      },
    ],
    [isContentWrapperBelow745, isContentWrapperBelow600]
  );

  const businessDocumentsImage: IBusinessDocumentView[] = useMemo(() => {
    if (!business?.business?.companyData?.businessDocuments) return [];

    return filterMostRecentValidBusinessDocument(
      business?.business?.companyData.businessDocuments,
      business
    )?.map((doc) => {
      return {
        title: getBusinessDocumentTitle(doc.name),
        imageUrl: doc.fileUrl,
      };
    });
  }, [business?.business?.companyData]);

  return (
    <Box>
      <Box
        css={{
          display: "flex",
          flexWrap: "wrap",
          columnGap: "32px",
          rowGap: "16px",
        }}
      >
        {businessDocumentsList.map(({ title, content }, index) => (
          <BusinessContentItem
            key={index}
            title={title}
            content={content}
            isContentWrapperBelow745={isContentWrapperBelow745}
            isContentWrapperBelow600={isContentWrapperBelow600}
          />
        ))}
      </Box>
      <Box
        className="flex flex-wrap mt-4"
        css={{
          columnGap: "32px",
          rowGap: "16px",
        }}
      >
        {businessDocumentsImage.map(({ title, imageUrl }, index) => {
          return (
            <BusinessDocumentView
              key={index}
              title={title}
              imageUrl={imageUrl}
              isContentWrapperBelow745={isContentWrapperBelow745}
              isContentWrapperBelow600={isContentWrapperBelow600}
              handleOpenBusinessDocumentImages={() =>
                handleOpenBusinessDocumentImages(index)
              }
            />
          );
        })}
      </Box>

      <ImageViewer
        imageDataList={businessDocumentsImage}
        isVisible={showBusinessDocumentImages}
        imageIndex={businessDocumentIndex}
        handleCloseImageViewer={handleCloseBusinessDocumentImages}
        handleImageIndexChange={handleBusinessDocumentImageIndexChange}
        uploaderName={business?.business?.email || "N/A"}
      />
    </Box>
  );
};
